import * as types from '../mutation-types'
import { Api } from '@/api/api'
import { getFormattedDate } from '@/utils/date'
import { getCheckPriceData } from '@/utils/bookingData'
import _ from 'lodash'
// initial state
export const initialState = () => {
  return {
    attributes: [],
    availableCampsites: [],
    availableExtras: [],
    campground: null,
    campsiteTypes: null,
    countries: [],
    currencies: [],
    languages: [],
    sessionTimeout: null,
    adyenVersion: '1.9.2',
    affiliateHtml: {},
    paymentInfos: {},
    status: {
      loading: false,
      success: false,
      error: false,
    },
    urls: null,
  }
}
let state = initialState()

// getters
const getters = {
  attributes: state => state.attributes,
  availableCampsites: state => state.availableCampsites,
  availableExtras: state => state.availableExtras,
  campground: state => state.campground,
  campsiteTypes: state => state.campsiteTypes,
  countries: state => state.countries,
  currencies: state => state.currencies,
  languages: state => state.languages,
  sessionTimeout: state => state.sessionTimeout,
  adyenVersion: state => state.adyenVersion,
  affiliateHtml: state => state.affiliateHtml,
  paymentInfos: state => state.paymentInfos,
  status: state => state.status,
  urls: state => state.urls,
}

// actions
const actions = {
  updateCampsite({ commit }, data) {
    commit(types.CAMPSITE_UPDATE_DATA, data)
  },
  getAttributes({ commit }) {
    if (state.campground) {
      Api.getCampsiteAttributes(state.campground.slug).then(result => {
        commit(types.CAMPGROUND_SET_ATTRIBUTES, result)
      })
    }
  },
  getCampgroundData({ dispatch, commit, rootGetters }, lang) {
    // if (state.status.loading) return

    commit(types.CAMPGROUND_STATUS_LOADING, true)
    return Api.getCampgroundData(lang)
      .then(result => {
        /* istanbul ignore else */
        if (!result.available) {
          commit(types.CAMPGROUND_STATUS_ERROR, false)
          commit(types.CAMPGROUND_STATUS_LOADING, false)
          commit(types.CAMPGROUND_STATUS_SUCCESS, true)
          return result
        }

        commit(types.CAMPGROUND_SET_AVAILABLE_EXTRAS, result.campground.extras)
        commit(types.CAMPGROUND_SET_CAMPGROUND, result.campground)
        commit(types.CAMPGROUND_SET_CAMPSITE_TYPES, result.campsite_types)
        commit(types.CAMPGROUND_SET_COUNTRIES, result.countries)
        commit(types.CAMPGROUND_SET_CURRENCIES, result.currencies)
        commit(types.CAMPGROUND_SET_LANGUAGES, result.languages)
        commit(types.CAMPGROUND_SET_URLS, result.urls)
        commit(types.CAMPGROUND_SET_SESSION_TIMEOUT, result.session_timeout)
        commit(types.CAMPGROUND_SET_ADYEN_VERSION, result.adyen_version)
        commit(types.CAMPGROUND_SET_AFFILIATE_HTML, result.affiliate_html)
        commit(types.CAMPGROUND_SET_PAYMENT_INFOS, result.payment_infos)

        // If the API returns draft booking data, apply it to the booking store
        const draft = result.draft_booking_data || result.draft_booking
        if (draft) {
          dispatch('booking/applyDraftBooking', draft, { root: true })
        }

        // set default currency if no currency is set
        const setCurrency = rootGetters['application/currency']
        /* istanbul ignore else */
        if (Object.keys(setCurrency).length === 0) {
          const currency = result.currencies.find(cur => cur.code === result.campground.currency)
          //commit(`application/${types.APP_SET_CURRENCY}`, currency, { root: true })
          dispatch('application/setCurrency', currency, { root: true })
        }

        // set default language
        const langSlug = rootGetters['application/langSlug']
        const language = result.languages.find(cur => cur.code === langSlug)
        dispatch('application/setLanguage', language, { root: true })

        // status handling
        commit(types.CAMPGROUND_STATUS_ERROR, false)
        commit(types.CAMPGROUND_STATUS_LOADING, false)
        commit(types.CAMPGROUND_STATUS_SUCCESS, true)
        return result
      })
      .catch(e => {
        commit(types.CAMPGROUND_STATUS_ERROR, true)
        commit(types.CAMPGROUND_STATUS_LOADING, false)
        commit(types.CAMPGROUND_STATUS_SUCCESS, false)
        throw e
      })
  },
  getExtraPrices({ commit, rootGetters }, value) {
    const user_currency = value ? value : rootGetters['application/currency']
    const urls = rootGetters['campground/urls']
    // get current chosen dates
    const dates = rootGetters['booking/dates']
    const dateFrom = getFormattedDate(dates[0], 'yyyy-MM-dd')
    const dateUntil = getFormattedDate(dates[1], 'yyyy-MM-dd')
    // should return in case of null or undefined dates to avoid 400 from /api/prices
    if (dateFrom == null || dateUntil == null) {
      return
    }
    const selectedPersons = rootGetters['booking/selectedPersons']
    const selectedCampsite = rootGetters['booking/selectedCampsite']

    let humanCount = 0
    let petCount = 0

    selectedPersons.forEach(selectedPerson => {
      const person = rootGetters['campground/campground'].persons.find(
        campgroundPerson => campgroundPerson.id === selectedPerson.id
      )

      if (person.person_type === 'pet') {
        petCount += selectedPerson.amount
      } else {
        humanCount += selectedPerson.amount
      }
    })

    const campsiteId = selectedCampsite && selectedCampsite.id ? selectedCampsite.id : null
    const queryParams = new URLSearchParams()

    if (user_currency.code) queryParams.set('currency_alt', user_currency.code)
    if (dateFrom) queryParams.set('date_from', dateFrom)
    if (dateUntil) queryParams.set('date_until', dateUntil)
    if (humanCount) queryParams.set('persons', humanCount)
    if (petCount) queryParams.set('pets', petCount)
    if (campsiteId) queryParams.set('acco_id', campsiteId)

    const url = `${urls.get_prices}?${queryParams.toString()}`
    /*
    const url = `${urls.get_prices}?date_from=${dateFrom}&date_until=${dateUntil}&currency_alt=${
      user_currency.code
    }`
*/
    return Api.getData(url)
      .then(result => {
        let extras = result.campground.extras

        // in case of currency alt, map currency_alt values to extra values
        if (result.currency_alt) {
          extras = result.campground.extras.map(extra => {
            extra.currency_alt = result.currency_alt.campground.extras.find(
              ex => ex.id === extra.id
            )
            return extra
          })
        }
        commit(types.CAMPGROUND_SET_AVAILABLE_EXTRAS, extras)

        // status handling
        commit(types.CAMPGROUND_STATUS_ERROR, false)
        commit(types.CAMPGROUND_STATUS_LOADING, false)
        commit(types.CAMPGROUND_STATUS_SUCCESS, true)
        return result
      })
      .catch(e => {
        commit(types.CAMPGROUND_STATUS_ERROR, true)
        commit(types.CAMPGROUND_STATUS_LOADING, false)
        commit(types.CAMPGROUND_STATUS_SUCCESS, false)
        throw e
      })
  },
  getAvailabilities({ commit, dispatch, rootGetters }, cu) {
    // get current chosen persons
    const persons = rootGetters['booking/selectedPersons']
    if (persons.length <= 0) return

    // get urls
    const availabilityUrl = rootGetters['campground/urls'].availability_check

    // get current chosen currency
    const currencyCode = cu ? cu.code : rootGetters['application/currency'].code

    // get current chosen dates
    const dates = rootGetters['booking/dates']
    const dateFrom = getFormattedDate(dates[0], 'yyyy-MM-dd')
    const dateUntil = getFormattedDate(dates[1], 'yyyy-MM-dd')

    // should return in case of null or undefined
    if (dateFrom == null || dateUntil == null) {
      return
    }

    // get current chosen campsite
    const selectedCampsite = rootGetters['booking/selectedCampsite']

    // get checkout data
    const checkout = rootGetters['booking/checkout']
    const booking_id = checkout.responseCheckout.booking_id

    const baseUrl = `${availabilityUrl}?date_from=${dateFrom}&date_until=${dateUntil}&get_priceinfo=1&currency_alt=${currencyCode}`

    const requestUrl = checkout.responseCheckout.booking_id
      ? `${baseUrl}&booking_id=${booking_id}`
      : baseUrl

    commit(types.CAMPGROUND_STATUS_LOADING, true)

    return Api.postData(requestUrl, { persons: persons })
      .then(result => {
        let availableCampsites = result.campsites

        const keyHelper = { number: 2, key: 'selectedCampsiteIsAvailable' }

        // if user has selected campsites
        if (selectedCampsite) {
          const filteredNotAvailable = result.campsites.filter(campsite => campsite.amount === 0)
          const selectedNotAvailable = filteredNotAvailable.some(
            campsite => campsite.id === selectedCampsite.id
          )

          // if the selected campsite is not available anymore
          if (selectedNotAvailable) {
            // this will show modal that selected campsite is not available and deletes selected from store
            dispatch('application/setStepStatus', { ...keyHelper, value: false }, { root: true })
            dispatch('booking/setSelectedCampsite', null, { root: true })
          } else {
            dispatch('application/setStepStatus', { ...keyHelper, value: true }, { root: true })
          }
        } else {
          dispatch('application/setStepStatus', { ...keyHelper, value: true }, { root: true })
        }

        // remember the data which leads to the price
        const request_data = { from: dateFrom, until: dateUntil, persons: persons }
        availableCampsites.forEach(campsite => {
          campsite.request_data = request_data
        })

        /* sort availableCampsites by amount see: https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Global_Objects/Array/sort */
        availableCampsites.sort((a, b) => {
          if (a.amount < b.amount) return 1
          if (a.amount > b.amount) return -1
          return 0
        })
        // commit available campsites
        commit(types.CAMPGROUND_SET_AVAILABLE_CAMPSITES, availableCampsites)

        // status handling
        commit(types.CAMPGROUND_STATUS_ERROR, false)
        commit(types.CAMPGROUND_STATUS_LOADING, false)
        commit(types.CAMPGROUND_STATUS_SUCCESS, true)
        return result
      })
      .catch(e => {
        commit(types.CAMPGROUND_STATUS_ERROR, true)
        commit(types.CAMPGROUND_STATUS_LOADING, false)
        commit(types.CAMPGROUND_STATUS_SUCCESS, false)
        throw e
      })
  },
  checkPrice({ commit, dispatch, rootGetters }, cu) {
    const campsite = rootGetters['booking/selectedCampsite']
    const currency = cu ? cu : rootGetters['application/currency']
    const dates = rootGetters['booking/dates']
    const selectedExtras = rootGetters['booking/selectedExtras']
    const selectedPersons = rootGetters['booking/selectedPersons']
    const urls = rootGetters['campground/urls']

    if (dates.length === 2 && selectedPersons.length > 0 && campsite !== null) {
      const booking_data = getCheckPriceData({
        dates,
        selectedCampsite: campsite,
        selectedExtras,
        selectedPersons,
      })

      // get checkout data
      const checkout = rootGetters['booking/checkout']
      const booking_id = checkout.responseCheckout.booking_id

      const baseUrl = `${urls.price_check}?&get_priceinfo=1&currency_alt=${currency.code}`

      const requestUrl = checkout.responseCheckout.booking_id
        ? `${baseUrl}&booking_id=${booking_id}`
        : baseUrl

      const keyHelper = { number: 2, key: 'selectedCampsiteIsAvailable' } // todo: gk

      return Api.postData(requestUrl, { booking_data })
        .then(result => {
          result.campsite_id = campsite.id
          result.request_data = booking_data
          dispatch('booking/setCampsiteData', result, { root: true })
          if (!result.error) {
            dispatch('booking/setPrice', result, { root: true })
          } else {
            dispatch('application/setStepStatus', { ...keyHelper, value: false }, { root: true })
            dispatch(
              'booking/setPrice',
              { subtotal: 0, total: 0, currency_alt: { total: 0 } },
              { root: true }
            )
          }
          return result
        })
        .catch(e => {
          commit(types.CAMPGROUND_STATUS_ERROR, true)
          commit(types.CAMPGROUND_STATUS_LOADING, false)
          commit(types.CAMPGROUND_STATUS_SUCCESS, false)
          throw e
        })
    } else {
      dispatch(
        'booking/setPrice',
        { subtotal: 0, total: 0, currency_alt: { total: 0 } },
        { root: true }
      )
    }
  },
  resetState({ commit }) {
    const initState = initialState()
    commit(types.CAMPGROUND_RESET_STATE, initState)
  },
}

// mutations
const mutations = {
  [types.CAMPGROUND_RESET_STATE](state, initState) {
    Object.keys(initState).forEach(key => {
      state[key] = initState[key]
    })
  },
  [types.CAMPGROUND_SET_AVAILABLE_CAMPSITES](state, payload) {
    payload.forEach(campsite => {
      const prevCampsite = state.availableCampsites.find(campsite2 => campsite2.id === campsite.id)
      if (prevCampsite) {
        if (_.isEqual(campsite.request_data, prevCampsite.request_data)) {
          campsite.amount = prevCampsite.amount
          campsite.prices = prevCampsite.prices
          campsite.reason_txt = prevCampsite.reason_txt
          campsite.cnc_price = prevCampsite.cnc_price
        }
      }
    })
    state.availableCampsites = payload
  },
  [types.CAMPGROUND_SET_AVAILABLE_EXTRAS](state, payload) {
    state.availableExtras = payload
  },
  [types.CAMPGROUND_SET_COUNTRIES](state, payload) {
    state.countries = []
    Object.entries(payload).forEach(([key, value]) => {
      state.countries.push({ code: key, label: value })
    })
  },
  [types.CAMPGROUND_SET_CAMPGROUND](state, payload) {
    state.campground = payload
  },
  [types.CAMPGROUND_SET_CAMPSITE_TYPES](state, payload) {
    state.campsiteTypes = payload
  },
  [types.CAMPGROUND_SET_CURRENCIES](state, payload) {
    state.currencies = payload
  },
  [types.CAMPGROUND_SET_LANGUAGES](state, payload) {
    state.languages = payload
  },
  [types.CAMPGROUND_SET_URLS](state, payload) {
    state.urls = payload
  },
  [types.CAMPGROUND_SET_SESSION_TIMEOUT](state, payload) {
    state.sessionTimeout = payload
  },
  [types.CAMPGROUND_SET_ADYEN_VERSION](state, payload) {
    state.adyenVersion = payload
  },
  [types.CAMPGROUND_SET_PAYMENT_INFOS](state, payload) {
    state.paymentInfos = payload
  },
  [types.CAMPGROUND_SET_AFFILIATE_HTML](state, payload) {
    state.affiliateHtml = payload
  },
  [types.CAMPGROUND_STATUS_LOADING](state, payload) {
    state.status.loading = payload
  },
  [types.CAMPGROUND_SET_ATTRIBUTES](state, payload) {
    state.attributes = payload
  },
  [types.CAMPGROUND_STATUS_SUCCESS](state, payload) {
    state.status.success = payload
  },
  [types.CAMPGROUND_STATUS_ERROR](state, payload) {
    state.status.error = payload
  },
  [types.CAMPSITE_SET_DATA](state, payload) {
    state.campsite_data = payload
  },
  [types.CAMPSITE_UPDATE_DATA](state, payload) {
    const campsite = state.availableCampsites.find(campsite => campsite.id === payload.campsiteId)
    const campsite2 = state.campground.campsites.find(
      campsite => campsite.id === payload.campsiteId
    )
    if (campsite === undefined) return
    if (payload.error) {
      campsite.amount = payload.amount
      campsite.reason_txt = payload.reason_text
      campsite2.amount = 0
      campsite.reason_txt = payload.reason_text
    } else {
      campsite.amount = 1
      campsite.reason_txt = ''
      if (campsite.cnc_price === undefined) {
        campsite.cnc_price = campsite.prices.total
      }
      campsite.prices.total = payload.price
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
